import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

/** Internal route: starts with / but not // (protocol-relative) and no external domain. */
function isInternalRoute(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//") && !href.startsWith("/http")
}

export function LinkRenderer({ element }: RendererProps) {
  const { resolveSymbol, navigate, dispatchEvent, hasEventBinding } = useRenderContext()
  const atomDefaults = useAtomDefaults("link")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const linkDataAttrs = {
    "data-atom-type": "link" as const,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
  }
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  const href = element.href ? String(resolveSymbol(String(element.href)) ?? "") : "#"
  const target = element.target as string | undefined
  const rel = element.rel as string | undefined ?? (target === "_blank" ? "noopener noreferrer" : undefined)

  const children = element.children?.map((child) => (
    <ElementRenderer key={child.id} element={child} />
  ))

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const hasTapBinding = Boolean(element.events?.tap)
      || hasEventBinding?.(element.id, "tap") === true
    // Bound links delegate navigation authority to ActionFlow. The external
    // events artifact is intentionally separate from the UI tree, so checking
    // only element.events would make generated link controls look functional
    // while never executing their flow.
    if (href === "#" || hasTapBinding) {
      e.preventDefault()
    }
    if (hasTapBinding || href !== "#") {
      dispatchEvent(element.id, "tap", { href })
    }
    // Internal client-side SPA navigation
    if (!hasTapBinding && navigate && isInternalRoute(href) && target !== "_blank") {
      e.preventDefault()
      navigate(href)
    }
  }

  return (
    <a
      href={href}
      target={target}
      rel={rel}
      onClick={handleClick}
      className={cn(element.className)}
      data-action="navigate"
      {...linkDataAttrs}
    >
      {children ?? text}
    </a>
  )
}
